import React, { useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';

function normalizeEmail(value) {
  return String(value || '').trim().toLowerCase();
}

function normalizeMobile(value) {
  const raw = String(value || '').trim();
  const digits = raw.replace(/[^\d]/g, '');
  // India-friendly: accept 10 digits (local) or 12 digits starting with 91
  if (digits.length === 10) return `+91${digits}`;
  if (digits.length === 12 && digits.startsWith('91')) return `+${digits}`;
  if (raw.startsWith('+') && digits.length >= 10) return `+${digits}`;
  return raw;
}

export default function LeadCaptureModal({ isOpen, onClose, selectedPlan, prefillEmail }) {
  const navigate = useNavigate();
  const modalTitleId = useId();
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [goal, setGoal] = useState('');
  const [pay299, setPay299] = useState('');
  const [justifiedPrice, setJustifiedPrice] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [botField, setBotField] = useState('');
  const [openedAt, setOpenedAt] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState('');

  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
  const shouldUseTurnstile = Boolean(turnstileSiteKey);

  useEffect(() => {
    if (!shouldUseTurnstile) return;
    if (!isOpen) return;

    window.onNotrackTurnstileSuccess = (token) => setTurnstileToken(token || '');
    window.onNotrackTurnstileExpired = () => setTurnstileToken('');
    window.onNotrackTurnstileError = () => setTurnstileToken('');

    return () => {
      try {
        delete window.onNotrackTurnstileSuccess;
        delete window.onNotrackTurnstileExpired;
        delete window.onNotrackTurnstileError;
      } catch {
        window.onNotrackTurnstileSuccess = undefined;
        window.onNotrackTurnstileExpired = undefined;
        window.onNotrackTurnstileError = undefined;
      }
    };
  }, [shouldUseTurnstile, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setMobile('');
      setGoal('');
      setPay299('');
      setJustifiedPrice('');
      setIsSubmitting(false);
      setSubmitError('');
      setBotField('');
      setOpenedAt(0);
      setTurnstileToken('');
      return;
    }
    setOpenedAt(Date.now());
    setEmail(prefillEmail || '');
    setTurnstileToken('');
  }, [isOpen, prefillEmail]);

  useEffect(() => {
    if (pay299 === 'yes') {
      setJustifiedPrice('');
    }
  }, [pay299]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (pay299 === 'no' && !justifiedPrice.trim()) {
      return;
    }
    // Honeypot: if filled, likely a bot.
    if (botField.trim()) {
      setSubmitError('Unable to submit. Please try again.');
      return;
    }
    // Time-to-submit: block ultra-fast automated submissions.
    if (openedAt && Date.now() - openedAt < 1500) {
      setSubmitError('Please wait a moment and try again.');
      return;
    }
    if (shouldUseTurnstile && !turnstileToken) {
      setSubmitError('Please complete the verification to continue.');
      return;
    }

    const emailNorm = normalizeEmail(email);
    const mobileNorm = normalizeMobile(mobile);

    const payload = {
      selectedPlan,
      email: emailNorm,
      mobile: mobileNorm,
      goal: String(goal || '').trim(),
      willing_pay_299: pay299,
      justified_monthly_price_inr: pay299 === 'no' ? justifiedPrice.trim() : null,
      turnstile_token: shouldUseTurnstile ? turnstileToken : null,
      source: {
        path: typeof window !== 'undefined' ? window.location.pathname : '',
        timestamp: new Date().toISOString(),
      },
    };

    // Temporary: log the exact payload you can POST to your backend.
    // Keep this until the API is wired up.
    // eslint-disable-next-line no-console
    console.log('[LeadCaptureModal] submit payload', payload);

    try {
      setSubmitError('');
      setIsSubmitting(true);
      const res = await fetch('http://localhost:3000/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        if (res.status === 409) {
          throw new Error('Email already exists.');
        }
        if (res.status === 408) {
          throw new Error('Mobile duplicate.');
        }
        if (res.status === 404) {
          throw new Error(
            'There is a technical issue currently. We are working on it. For help, contact mobile: 9959167972.'
          );
        }
        let details = '';
        try {
          details = await res.text();
        } catch {
          details = '';
        }
        throw new Error(details || `Request failed (${res.status})`);
      }

      onClose();
      navigate('/thank-you', { replace: true, state: { selectedPlan } });
    } catch (err) {
      setSubmitError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const showPriceFollowUp = pay299 === 'no';

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={modalTitleId}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal modal--lead" role="document">
        <div className="modal-head">
          <div className="modal-title-wrap">
            <div className="modal-kicker">Selected: {selectedPlan}</div>
            <div className="modal-title" id={modalTitleId}>
              Lock early access
            </div>
          </div>
          <button className="modal-x" type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <p className="body modal-lead-copy">
          We’re in private beta. Share a few details so we can prioritise your invite and pricing.
        </p>

        <form className="modal-form" onSubmit={handleSubmit}>
          {/* Honeypot (anti-spam): should remain empty. */}
          <label className="field hp-field" aria-hidden="true">
            <span className="label">Company</span>
            <input
              tabIndex={-1}
              autoComplete="off"
              value={botField}
              onChange={(e) => setBotField(e.target.value)}
              name="company"
            />
          </label>

          <label className="field">
            <span className="label">Email</span>
            <input
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              required
              placeholder="you@domain.com"
              autoComplete="email"
              autoFocus
            />
          </label>

          <label className="field">
            <span className="label">Mobile number</span>
            <input
              name="mobile"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              type="tel"
              inputMode="tel"
              required
              placeholder="+91 98765 43210"
              autoComplete="tel"
            />
          </label>

          <label className="field">
            <span className="label">Your goal</span>
            <textarea
              name="goal"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              required
              rows={3}
              placeholder="e.g. Lose 8 kg sustainably, feel better at work, stop yo-yo dieting…"
            />
          </label>

          <fieldset className="field field-radios">
            <legend className="label">Would you pay ₹299 per month for Notrack?</legend>
            <div className="radio-row">
              <label className="radio-pill">
                <input
                  type="radio"
                  name="willing_pay_299"
                  value="yes"
                  checked={pay299 === 'yes'}
                  onChange={() => setPay299('yes')}
                  required
                />
                <span>Yes</span>
              </label>
              <label className="radio-pill">
                <input
                  type="radio"
                  name="willing_pay_299"
                  value="no"
                  checked={pay299 === 'no'}
                  onChange={() => setPay299('no')}
                />
                <span>No</span>
              </label>
            </div>
          </fieldset>

          {showPriceFollowUp ? (
            <label className="field field-follow-up">
              <span className="label">What monthly price would feel justified? (₹)</span>
              <input
                name="justified_monthly_price_inr"
                value={justifiedPrice}
                onChange={(e) => setJustifiedPrice(e.target.value)}
                required
                placeholder="e.g. ₹149, or a range you’d consider"
                autoComplete="off"
              />
            </label>
          ) : null}

          {shouldUseTurnstile ? (
            <div className="field turnstile-field">
              <span className="label">Verification</span>
              <div
                className="cf-turnstile"
                data-sitekey={turnstileSiteKey}
                data-callback="onNotrackTurnstileSuccess"
                data-expired-callback="onNotrackTurnstileExpired"
                data-error-callback="onNotrackTurnstileError"
              />
            </div>
          ) : null}

          <button className="btn btn-primary wide" type="submit" disabled={isSubmitting || (shouldUseTurnstile && !turnstileToken)}>
            {isSubmitting ? 'Submitting…' : 'Submit & join waitlist'}
          </button>
          {submitError ? (
            <div className="modal-micro modal-error" role="alert">
              {submitError}
            </div>
          ) : null}
          <div className="modal-micro">No credit card required. We’ll only use this to reach you about early access.</div>
        </form>
      </div>
    </div>
  );
}
